// src/components/common/Button.jsx
import React from "react";

/**
 * Local utility to join class names safely
 * (prevents undefined/null from appearing in final class string)
 */
function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

/**
 * Button
 * Props:
 *  - variant: "primary" | "secondary" | "ghost"
 *  - size: "sm" | "md" | "lg"
 *  - loading: boolean
 *  - disabled: boolean
 *  - children
 */
export default function Button({
  variant = "primary",
  size = "md",
  loading = false,
  disabled = false,
  children,
  className = "",
  ...rest
}) {
  const base =
    "btn-anim inline-flex items-center justify-center font-semibold rounded-md transition";

  const variants = {
    primary:
      "bg-green-600 text-white border border-transparent hover:bg-green-700 dark:bg-green-600 dark:hover:bg-green-700",
    secondary:
      "bg-white text-gray-800 border border-green-600 hover:bg-green-50 dark:bg-gray-800 dark:text-gray-200 dark:border-green-500 dark:hover:bg-gray-700",
    ghost:
      "bg-transparent text-gray-800 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800",
    outline:
      "bg-transparent text-gray-700 border border-gray-300 hover:bg-gray-50 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-800",
    warning:
      "bg-orange-500 text-white border border-transparent hover:bg-orange-600 dark:bg-orange-600 dark:hover:bg-orange-700",
    danger:
      "bg-red-600 text-white border border-transparent hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-700",
  };

  const sizes = {
    xs: "px-2 py-1 text-xs",
    sm: "px-3 py-1.5 text-sm",
    md: "px-4 py-2 text-sm",
    lg: "px-5 py-3 text-base",
  };

  return (
    <button
      className={cn(
        base,
        variants[variant],
        sizes[size],
        disabled || loading ? "opacity-60 cursor-not-allowed" : "",
        className
      )}
      disabled={disabled || loading}
      {...rest}
    >
      {loading ? <span className="animate-fade-in">Loading...</span> : children}
    </button>
  );
}
